import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import cx from "classnames";
import cn from "variant-classnames";
import { toolkitCx } from "../../../constants";
import ConditionalWrapper from "../../atoms/ConditionalWrapper";
import Text from "../../atoms/Text/Text";
import Tooltip from "../../organisms/Tooltip";
const variants = {
    $all: "flex items-center justify-between",
    minSpacing: {
        xs: "gap-1",
        sm: "gap-2",
        md: "gap-4",
        lg: "gap-6",
        xl: "gap-8",
    },
};
const tooltipFillMap = {
    current: toolkitCx.layout.text.current,
    silent: toolkitCx.layout.text.silent,
    muted: toolkitCx.layout.text.muted,
    normal: toolkitCx.layout.text.default,
    primary: toolkitCx.layout.text.primary,
    forceLight: toolkitCx.layout.text.light,
    forceDark: toolkitCx.layout.text.dark,
    error: toolkitCx.layout.text.error,
    secondary: toolkitCx.layout.text.secondary,
    success: toolkitCx.layout.text.success,
    warning: toolkitCx.layout.text.warning,
    info: toolkitCx.layout.text.info,
};
const tooltipTriggerSizeMap = {
    xs: "h-3 w-3",
    sm: "h-[0.875rem] w-[0.875rem]",
    md: "w-4 h-4",
    lg: ":h-[1.125rem] w-[1.125rem]",
    xl: "w-5 h-5",
};
/**
 * A component that renders a single field-value pair inside a FlexList.
 *
 * @component
 *
 * @param {IFlexListItemProps} props The component props.
 * @param {string} [props.className] The CSS class to apply to the component.
 * @param {IValue} props.field The field object containing a label, size and weight.
 * @param {string} [props.minSpacing="md"] The minimum spacing to use between the field and value components.
 * @param {string} [props.tooltip] The text to use as the tooltip for the component.
 * @param {string} [props.tooltipCx="h-4 w-4"] The CSS class to apply to the tooltip trigger icon.
 * @param {IValue} props.value The value object containing a label, size, weight and tag.
 *
 * @returns {JSX.Element} The rendered `FlexListItem` component.
 */
const FlexListItem = ({ className, field, minSpacing = "md", tooltip, tooltipCx = "h-4 w-4", value, ...rest }) => {
    const classes = cn(variants, { className, minSpacing });
    return (_jsxs("div", { ...rest, className: classes, children: [_jsx("div", { className: "flex items-center gap-1", children: _jsxs(ConditionalWrapper, { condition: typeof field.label === "string", wrapper: (children) => (_jsx(Text, { className: "inline-flex items-center gap-1", tag: field.tag ?? "span", size: field.size ?? "sm", variant: field.variant, weight: field.weight, children: children })), children: [field.label, tooltip && (_jsx(Tooltip, { tooltipLabel: tooltip, trigger: _jsx(InfoCircledIcon, { className: cx("cursor-pointer", tooltipCx, tooltipFillMap[field.variant ?? "normal"]), height: tooltipTriggerSizeMap[field.size ?? "sm"], width: tooltipTriggerSizeMap[field.size ?? "sm"] }) }))] }) }), _jsx(_Fragment, { children: typeof value.label === "string" ? (_jsx(Text, { tag: value.tag ?? "span", size: value.size ?? "sm", variant: value.variant, weight: value.weight, children: value.label })) : (_jsx(_Fragment, { children: value.label })) })] }));
};
export default FlexListItem;
//# sourceMappingURL=FlexListItem.js.map